import React, { useState, useMemo } from "react";
import { PositionView } from "./PositionView";
import { OrdersView } from "./OrdersView";
import { AccountView } from "./AccountView";
import { RawPositionsView } from "./RawPositionsView";
import { BrokerRawPositionsView } from "./BrokerRawPositionsView";
import { BacktestView } from "./BacktestView";

import { usePositionsView } from "../hooks/usePositionsView";
import { useOrdersView } from "../hooks/useOrdersView";
import { useAccountView } from "../hooks/useAccountView";

import { usePositionStore } from "../contexts/LTPStore/usePositionsStore";
import { useOrderStore } from "../contexts/LTPStore/useOrdersStore";
import { useRawPositionStore } from "../contexts/LTPStore/useRawPositionStore";
import { useGlobalUserMappings } from "../hooks/useGlobalHooks";
import RejectedOrderView from "../components/RejectedOrderView"
import { useRejectedOrdersView } from "../hooks/useRejectedOrdersView";
import { useCommoditiesView } from "../hooks/useCommoditiesView";
import { usePledgePlusOrdersView } from "../hooks/usePledgePlusOrdersView";
import MiniDropdownTab from "../components/DashboardNewComponents/MiniDropdownTab"
import { ExitRawPositionsView } from "./ExitRawPositionsView";
import { CommoditiesPositionsView } from "./CommoditiesPositionsView";
import { PledgePlusOrdersView } from "./PledgePlusOrdersView";
import { useRejectedOrderStore } from "../contexts/LTPStore/useRejectedOrderStore";
import { AlertCircle, Settings, RefreshCw, Activity, Wifi, WifiOff, Zap, Bell, Radio } from 'lucide-react';
import apiClient from "../utils/apiClient";
import server from "../utils/serverConfig";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { useWebSocketStatusStore } from "../contexts/LTPStore/useWebSocketStatusStore";
import WebSocketManager from "../contexts/websocketManager";
import { usePotentialTrades } from "../hooks/usePotentialTrades";
import NudgeOverlay from "./NudgeOverlay";
import PotentialTradesModal from "./PotentialTradesModal";
import SignalToastOverlay from "./SignalToastOverlay";

export function DashboardNew() {
  const [activeTab, setActiveTab] = useState("positions");
  const { refreshAll, pingWebSocket, reconnectWebSocket, users } = useGlobalData();
  const wsStatus = useWebSocketStatusStore((s) => s.status);

  // --- Market Clock ---
  const [clock, setClock] = useState(() => new Date());
  React.useEffect(() => {
    // Sync to next real second boundary so clock never lags
    const msToNextSecond = 1000 - (Date.now() % 1000);
    let interval;
    const timeout = setTimeout(() => {
      setClock(new Date());
      interval = setInterval(() => setClock(new Date()), 1000);
    }, msToNextSecond);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  const istTime = clock.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const istDate = clock.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
    day: "2-digit",
    month: "short",
  });

  // Derive market status from IST hours/minutes
  const istNow = new Date(clock.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const istHours = istNow.getHours();
  const istMinutes = istNow.getMinutes();
  const totalIstMins = istHours * 60 + istMinutes;
  const isWeekend = istNow.getDay() === 0 || istNow.getDay() === 6;
  const marketOpen = totalIstMins >= 9 * 60 + 15 && totalIstMins < 15 * 60 + 30;
  const preMarket = totalIstMins >= 9 * 60 && totalIstMins < 9 * 60 + 15;
  const postMarket = totalIstMins >= 15 * 60 + 30 && totalIstMins < 16 * 60;
  const marketStatus = isWeekend ? "closed"
    : marketOpen ? "open"
    : preMarket ? "pre"
    : postMarket ? "post"
    : "closed";
  const marketStatusLabel = { open: "Market Open", pre: "Pre-Market", post: "Post-Market", closed: "Market Closed" }[marketStatus];
  const clockColor = marketStatus === "open" ? "text-emerald-400" : marketStatus === "pre" || marketStatus === "post" ? "text-amber-400" : "text-gray-400";
  const dotColor = marketStatus === "open" ? "bg-emerald-400" : marketStatus === "pre" || marketStatus === "post" ? "bg-amber-400" : "bg-gray-500";

  const handleReconnect = () => {
    reconnectWebSocket();
    WebSocketManager.getInstance().reconnectWebSocket();
  };

  const handlePing = () => {
    pingWebSocket();
    WebSocketManager.getInstance().pingWebSocket();
  };

  // -------------------------------------------------------
  // 1️⃣ POSITIONS from Zustand
  // -------------------------------------------------------
  const positionsRaw = usePositionStore((s) => s.positions);
  const positionsVersion = usePositionStore((s) => s.version);  // 🔥 new version
  const positionsLoading = usePositionStore((s) => s.loading ?? false);

  // ensure array format
  const positionsArray = Array.isArray(positionsRaw)
    ? positionsRaw
    : positionsRaw && typeof positionsRaw === "object"
      ? Object.values(positionsRaw)
      : [];

  const rawBrokerPositions = useRawPositionStore((s) => s.rawPositions) || [];


  // -------------------------------------------------------
  // 2️⃣ ORDERS from Zustand
  // -------------------------------------------------------
  const orders = useOrderStore((s) => s.orders) || [];
  const RejectedOrder = useRejectedOrderStore((s) => s.orders) || [];
  const rejectOrdersLoading = useRejectedOrderStore((s) => s.loading ?? false);
  const ordersUpdateVersion = useOrderStore((s) => s.updateVersion ?? 0);
  const ordersLoading = useOrderStore((s) => s.loading ?? false);

  // console.log("Orders HERE I GOT_------",orders)
  // -------------------------------------------------------
  // 3️⃣ USER MAPPINGS
  // -------------------------------------------------------
  const { userMappings, loading: userMappingsLoading } =
    useGlobalUserMappings();



  const safeUserMappings = Array.isArray(userMappings) ? userMappings : [];

  // -------------------------------------------------------
  // 4️⃣ PROCESS POSITION + ORDER VIEWS
  // -------------------------------------------------------
  const positionViewData = usePositionsView(
    positionsArray,
    orders,
    safeUserMappings,
    positionsVersion
  );

  const orderViewData = useOrdersView(
    orders,
    safeUserMappings,
    ordersUpdateVersion,
    users
  );



  const rejectedOrdersData = useRejectedOrdersView(
    RejectedOrder,
    safeUserMappings,
    ordersUpdateVersion,
  );

  const commoditiesViewData = useCommoditiesView(
    positionsArray,
    orders,
    safeUserMappings,
    positionsVersion
  );

  const pledgePlusOrdersViewData = usePledgePlusOrdersView(
    orders,
    safeUserMappings,
    positionsArray
  );




  const accountViewData = useAccountView();

  // console.log("ORDER DATA OUT0000ER_----",accountViewData);
  // -------------------------------------------------------
  // 5️⃣ LOADING + COUNTS
  // -------------------------------------------------------
  const isPositionsInitialLoading =
    positionsLoading && positionsArray.length === 0;

  const isOrdersInitialLoading =
    ordersLoading && orders.length === 0;

  const indexPositionsCount =
    positionsArray.filter((p) => {
      const s = (p.symbol || "").toUpperCase();
      const cleanSymbol = s.replace("NSE:", "");

      // 1. Exclude MCX, NFO, BFO, and Fyers futures (same as usePositionsView)
      if (s.includes("MCX") || s.includes("NFO") || s.includes("BFO") || s.endsWith("FUT")) return false;

      // 2. Exclude closed positions (qty 0)
      const netQty = parseFloat(p.net_qty ?? p.qty ?? 0);
      if (Math.abs(netQty) === 0) return false;

      // 3. Include only specific indices supported by usePositionsView
      return (
        cleanSymbol.includes("BANKNIFTY") ||
        cleanSymbol.includes("FINNIFTY") ||
        cleanSymbol.includes("MIDCPNIFTY") ||
        cleanSymbol.includes("SENSEX") ||
        cleanSymbol.includes("NIFTY")
      );
    }).length || 0;


  const hasNegativeSell = positionsArray.some(p => {
    const side = (p.side === 1 || p.side === '1' || String(p.side).toLowerCase().startsWith('b')) ? 'BUY' :
      (p.side === 2 || p.side === -1 || p.side === '2' || String(p.side).toLowerCase().startsWith('s')) ? 'SELL' : '';
    const qty = parseFloat(p.net_qty ?? p.qty ?? 0);
    const symbol = (p.symbol || "").toUpperCase();
    return side === 'SELL' && qty < 0 && !symbol.startsWith("MCX") && !symbol.endsWith("FUT");
  });

  // console.log("Over view data---",orderViewData);



  const [showSettings, setShowSettings] = useState(false);

  const [sellModule, setSellModule] = useState(1);
  const [exitModule, setExitModule] = useState(1);
  const [sellXtsEnabled, setSellXtsEnabled] = useState(true); // true = ON, false = OFF
  const [sellXtsAnuragEnabled, setSellXtsAnuragEnabled] = useState(true); // true = ON, false = OFF
  const [sellFyersEnabled, setSellFyersEnabled] = useState(true); // true = ON, false = OFF
  const [sellDhanHqEnabled, setSellDhanHqEnabled] = useState(true); // true = ON, false = OFF
  const [sellNubraEnabled, setSellNubraEnabled] = useState(true); // true = ON, false = OFF
  const [sellPledgePlusDhanHqEnabled, setSellPledgePlusDhanHqEnabled] = useState(true);
  const [sellPledgePlusFyersEnabled, setSellPledgePlusFyersEnabled] = useState(true);
  const [sellPledgePlusZerodhaEnabled, setSellPledgePlusZerodhaEnabled] = useState(true);

  // Fetch initial settings from backend (optional but recommended)
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await apiClient.get(`${server.godseye.baseUrl}optimus/settings`);
        setSellModule(parseInt(res.data.data.sell_module ?? 1));
        setExitModule(parseInt(res.data.data.exit_module ?? 1));
      } catch (err) {
        console.error("Settings fetch failed", err);
      }

      // Fetch Sell XTS (Optimus) status from Optimus server
      try {
        const xtsRes = await apiClient.get("https://optimus.dhanss.server.sypto.xyz/optimus/settings/status/sell_xts");
        setSellXtsEnabled(xtsRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell XTS status fetch failed", err);
        // Defaults to true (enabled) if fetch fails — safe fallback
      }

      // Fetch Sell XTS Anurag (Optimus) status from Optimus Anurag server
      try {
        const xtsResAnurag = await apiClient.get("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/status/sell_xts");
        setSellXtsAnuragEnabled(xtsResAnurag.data.enabled ?? true);
      } catch (err) {
        console.error("Sell XTS Anurag status fetch failed", err);
        // Defaults to true (enabled) if fetch fails
      }

      // Fetch Sell Fyers (Optimus) status from Godseye server
      try {
        const fyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_fyers");
        setSellFyersEnabled(fyersRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell Fyers status fetch failed", err);
        // Defaults to true (enabled) if fetch fails
      }

      // Fetch Sell DhanHQ (Optimus) status from Godseye server
      try {
        const dhanHqRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_dhanhq");
        setSellDhanHqEnabled(dhanHqRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell DhanHQ status fetch failed", err);
      }

      // Fetch Sell Nubra status from Godseye server
      try {
        const nubraRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_nubra");
        setSellNubraEnabled(nubraRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell Nubra status fetch failed", err);
      }

      // Fetch Pledge Plus sell statuses
      try {
        const ppDhanRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhanhq");
        setSellPledgePlusDhanHqEnabled(ppDhanRes.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus DhanHQ status fetch failed", err); }
      try {
        const ppFyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_fyers");
        setSellPledgePlusFyersEnabled(ppFyersRes.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus Fyers status fetch failed", err); }
      try {
        const ppZerodhaRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_zerodha");
        setSellPledgePlusZerodhaEnabled(ppZerodhaRes.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus Zerodha status fetch failed", err); }
    };

    loadSettings();
  }, []);


  const [nudgeThreshold, setNudgeThreshold] = useState(50);
  const { groupedNudges } = usePotentialTrades(Number(nudgeThreshold) || 0, "both", positionViewData.symbolStrategyBaseQty);

  // Nudge O enable/disable — defaults OFF, persisted in localStorage
  const [nudgesOEnabled, setNudgesOEnabled] = useState(() => {
    try { return localStorage.getItem("nudgesOEnabled") === "true"; }
    catch { return false; }
  });
  const handleToggleNudgesO = () => {
    setNudgesOEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem("nudgesOEnabled", String(next)); } catch {}
      return next;
    });
  };

  // Nudge P enable/disable — defaults OFF, persisted in localStorage
  const [nudgesPEnabled, setNudgesPEnabled] = useState(() => {
    try { return localStorage.getItem("nudgesPEnabled") === "true"; }
    catch { return false; }
  });
  const handleToggleNudgesP = () => {
    setNudgesPEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem("nudgesPEnabled", String(next)); } catch {}
      return next;
    });
  };

  // Global Potential Trades Modal
  const [isPotentialTradesModalOpen, setIsPotentialTradesModalOpen] = useState(false);

  const [dismissedNudges, setDismissedNudges] = useState([]);
  const activeNudges = useMemo(() => {
    return groupedNudges.filter(n => {
      if (dismissedNudges.includes(n.id)) return false;
      if (n.nudgeCategory === "O" && !nudgesOEnabled) return false;
      if (n.nudgeCategory === "P" && !nudgesPEnabled) return false;
      return nudgesOEnabled || nudgesPEnabled;
    });
  }, [groupedNudges, dismissedNudges, nudgesOEnabled, nudgesPEnabled]);

  const handleDismissNudge = (id) => {
    setDismissedNudges(prev => [...prev, id]);
  };

  // Reset dismissed nudges every 5 mins
  React.useEffect(() => {
    const interval = setInterval(() => setDismissedNudges([]), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // -------------------------------------------------------
  // 🔔 SIGNAL UPDATE ALERT
  // -------------------------------------------------------
  const [signalAlertEnabled, setSignalAlertEnabled] = useState(() => {
    try { return localStorage.getItem("signalAlertEnabled") === "true"; }
    catch { return false; }
  });

  const handleToggleSignalAlert = () => {
    setSignalAlertEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem("signalAlertEnabled", String(next)); } catch {}
      if (next) {
        // Fetch immediately so the user sees the latest signal status on toggle-on
        fetchSignalUpdate();
      }
      return next;
    });
  };

  // Signal toast notifications (shown in overlay)
  const [signalToasts, setSignalToasts] = useState([]);

  const dismissSignalToast = (id) => {
    setSignalToasts(prev => prev.filter(t => t.id !== id));
  };

  // Helper: get today's date in YYYY-MM-DD format (IST)
  const getTodayIST = () => {
    const now = new Date();
    return now.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // "YYYY-MM-DD"
  };

  // Helper: parse "DD Mon YYYY, HH:MM:SS" or ISO-like timestamps from API
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    // Try direct Date parse first
    const d = new Date(ts);
    if (!isNaN(d)) return d;
    // Try format: "24 Apr 2026, 14:15:00"
    const match = ts.match(/(\d{1,2})\s+(\w+)\s+(\d{4}),?\s+(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      return new Date(`${match[2]} ${match[1]} ${match[3]} ${match[4]}:${match[5]}:${match[6]} GMT+0530`);
    }
    return null;
  };

  // Core fetch: called at HH:15:20 IST
  const fetchSignalUpdate = React.useCallback(async () => {
    const todayIST = getTodayIST();
    const nowIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const currentHour = nowIST.getHours(); // e.g. 13 when fetching at 13:15:20
    // Expected candle timestamp hour = currentHour - 1
    const expectedHour = currentHour - 1;
    // Build expected timestamp string pattern: "YYYY-MM-DD THH:15:00" in IST
    // We'll compare using hour only for robustness
    const expectedMinute = 15;

    const endpoints = [
      { label: "V5",   url: `https://godseye.server.sypto.xyz/signal_update?start_date=${todayIST}&end_date=${todayIST}&table_name=options_v5_fyers_60min` },
      { label: "V5_2", url: `https://godseye.server.sypto.xyz/signal_update?start_date=${todayIST}&end_date=${todayIST}&table_name=options_v5_2_fyers_60min` },
      { label: "V6",   url: `https://godseye.server.sypto.xyz/signal_update?start_date=${todayIST}&end_date=${todayIST}&table_name=options_v6_fyers_60min` },
    ];

    const results = await Promise.all(
      endpoints.map(async ({ label, url }) => {
        try {
          const res = await apiClient.get(url);
          const json = res.data;
          // API may return array or { data: [...] }
          const rows = Array.isArray(json) ? json : (json.data || json.result || []);
          // Look for a row whose time_stamp matches expectedHour:15:00
          const matched = rows.some(row => {
            const ts = row.time_stamp || row.timestamp || row.TimeStamp;
            const d = parseTimestamp(ts);
            if (!d) return false;
            const istD = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
            return istD.getHours() === expectedHour && istD.getMinutes() === expectedMinute;
          });
          return { label, received: matched };
        } catch {
          return { label, received: false, error: true };
        }
      })
    );

    const fetchedAt = new Date().toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    });
    const expectedTs = `${String(expectedHour).padStart(2, "0")}:15:00`;

    const id = `signal-${Date.now()}`;
    setSignalToasts([{ id, results, fetchedAt, expectedTs }]);
  }, []);

  // Scheduler: fire at HH:15:20 IST, hours 10–15 (10:15:20 through 15:15:20)
  React.useEffect(() => {
    if (!signalAlertEnabled) return;

    const tick = () => {
      const nowIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
      const h = nowIST.getHours();
      const m = nowIST.getMinutes();
      const s = nowIST.getSeconds();
      // Fire from 10:15:20 to 15:15:20 (at HH:15:20)
      if (m === 15 && s === 20 && h >= 10 && h <= 15) {
        fetchSignalUpdate();
      }
    };

    // Sync to next second boundary
    const msToNextSecond = 1000 - (Date.now() % 1000);
    let interval;
    const timeout = setTimeout(() => {
      tick();
      interval = setInterval(tick, 1000);
    }, msToNextSecond);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [signalAlertEnabled, fetchSignalUpdate]);

  // hit backend toggle APIs
  const handleToggleSell = async () => {
    try {
      const res = await apiClient.post(`${server.godseye.baseUrl}optimus/settings/toggle/sell`);
      setSellModule(res.data.sell_module);
    } catch (e) {
      console.error("Toggle sell failed", e);
    }
  };

  const handleToggleExit = async () => {
    try {
      const res = await apiClient.post(`${server.godseye.baseUrl}optimus/settings/toggle/exit`);
      setExitModule(res.data.exit_module);
    } catch (e) {
      console.error("Toggle exit failed", e);
    }
  };

  const handleToggleSellXts = async () => {
    try {
      const res = await apiClient.post("https://optimus.dhanss.server.sypto.xyz/optimus/settings/toggle/sell_xts");
      // API returns { current: "true" | "false" }
      setSellXtsEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_xts failed", e);
    }
  };

  const handleToggleSellXtsAnurag = async () => {
    try {
      const res = await apiClient.post("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/toggle/sell_xts");
      // API returns { current: "true" | "false" }
      setSellXtsAnuragEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_xts_anurag failed", e);
    }
  };

  const handleToggleSellFyers = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_fyers");
      // API returns { current: "true" | "false" }
      setSellFyersEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_fyers failed", e);
    }
  };

  const handleToggleSellDhanHq = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_dhanhq");
      // API returns { current: "true" | "false" }
      setSellDhanHqEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_dhanhq failed", e);
    }
  };

  const handleToggleSellNubra = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_nubra");
      // API returns { current: "true" | "false" }
      setSellNubraEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_nubra failed", e);
    }
  };

  const handleToggleSellPledgePlusDhanHq = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_dhanhq");
      setSellPledgePlusDhanHqEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_dhanhq failed", e); }
  };

  const handleToggleSellPledgePlusFyers = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_fyers");
      setSellPledgePlusFyersEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_fyers failed", e); }
  };

  const handleToggleSellPledgePlusZerodha = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_zerodha");
      setSellPledgePlusZerodhaEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_zerodha failed", e); }
  };
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    module: "", // "sell" or "exit"
    action: "", // "enable" or "disable"
    onConfirm: null, // function to call if user confirms
    loading: false,
  });


  const tabs = [
    {
      id: "positions",
      label: "Optimus Position View",
      count: indexPositionsCount,
      loading: isPositionsInitialLoading,
    },
    {
      id: "orders",
      label: "Optimus Order View",
      count: orderViewData.buyOrdersCount,
      loading: isOrdersInitialLoading,
    },
    {
      id: "commodities",
      label: "Pledge+ Position View",
      count: commoditiesViewData?.data?.length || 0,
      loading: isPositionsInitialLoading,
    },

    {
      id: "pledge_plus_orders",
      label: "Pledge+ Orders View",
      count: pledgePlusOrdersViewData?.data?.length || 0,
      loading: isOrdersInitialLoading,
    },
    {
      id: "raw",
      label: "Position Manager",
      count: positionsArray.length,
      loading: isPositionsInitialLoading,
    },
    {
      id: "broker_raw",
      label: "Broker Raw Positions",
      count: rawBrokerPositions.length,
      loading: false,
    },
    {
      id: "account",
      label: "Account View",
      count: safeUserMappings.length,
      loading: userMappingsLoading,
    },

    {
      id: "rejected",
      label: "Rejected Order View",
      count: rejectedOrdersData.length,
      loading: rejectOrdersLoading,
    },
    {
      id: "exitposition",
      label: "Exit Position View",
      count: positionsArray.length,
      loading: isPositionsInitialLoading,
    },
    // {
    //   id: "backtest",
    //   label: "Backtest",
    //   count: 0,
    //   loading: false,
    // },

  ];

  // console.log("Position View Data-----",positionViewData);
  // -------------------------------------------------------
  // 7️⃣ RENDER
  // -------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#0f1116] flex flex-col">
      {/* ------------------------- HEADER ------------------------- */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            height: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(55, 65, 81, 0.5);
            border-radius: 9999px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(75, 85, 99, 1);
          }
        `}
      </style>
      <header className="bg-[#1a1d29] border-b border-gray-800 sticky top-0 z-10">
        <div className="flex items-center px-4 md:px-6 py-2 w-full">
          {/* LEFT TABS - scrollable */}
          <div className="flex-1 overflow-x-auto pb-3 custom-scrollbar">
            <div className="flex gap-1 md:gap-2 min-w-max">
              {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                const isAlertState = tab.id === 'raw' && hasNegativeSell;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                    relative flex items-center gap-2 px-4 md:px-6 py-2 text-sm md:text-base font-medium transition-all duration-200 outline-none
                    ${isActive ? "text-white bg-[#0f1116]" : "text-gray-400 hover:text-gray-200 hover:bg-[#14161f]"}
                    ${isAlertState && !isActive ? "bg-red-500/5 text-red-400 hover:bg-red-500/10 hover:text-red-300" : ""}
                  `}
                  >
                    {isAlertState && <AlertCircle className="w-4 h-4 text-red-500 animate-pulse" />}
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm
                      ${isAlertState ? "bg-red-500/20 text-red-400 border border-red-500/30" :
                          isActive ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400 border border-gray-700"}`}>
                        {tab.count}
                      </span>
                    )}
                    {isActive && (
                      <div className={`absolute bottom-0 left-0 right-0 h-0.5 shadow-[0_-1px_6px_rgba(0,0,0,0.5)]
                      ${isAlertState ? "bg-red-500 shadow-red-500/50" : "bg-blue-600 shadow-blue-500/50"}`}></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>






          {/* RIGHT SETTINGS ICON - fixed */}
          <div className="ml-4 relative flex-shrink-0">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 rounded-md hover:bg-[#14161f] transition text-gray-300 hover:text-white"
            >
              <Settings className="w-5 h-5" />
            </button>

            {showSettings && (
              <div className="absolute right-0 mt-2 w-56 bg-[#1a1d29] border border-gray-700 rounded-xl shadow-xl p-3 z-50">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-gray-300 text-sm font-semibold">Optimus Controls</h3>
                  <button
                    onClick={async () => {
                      try {
                        const res = await apiClient.get(`${server.godseye.baseUrl}optimus/settings`);
                        setSellModule(parseInt(res.data.data.sell_module ?? 1));
                        setExitModule(parseInt(res.data.data.exit_module ?? 1));
                      } catch (err) {
                        console.error("Failed to refresh settings", err);
                      }
                      try {
                        const xtsRes = await apiClient.get("https://optimus.dhanss.server.sypto.xyz/optimus/settings/status/sell_xts");
                        setSellXtsEnabled(xtsRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell XTS status", err);
                      }
                      try {
                        const xtsResAnurag = await apiClient.get("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/status/sell_xts");
                        setSellXtsAnuragEnabled(xtsResAnurag.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell XTS Anurag status", err);
                      }
                      try {
                        const fyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_fyers");
                        setSellFyersEnabled(fyersRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell Fyers status", err);
                      }
                      try {
                        const dhanHqRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_dhanhq");
                        setSellDhanHqEnabled(dhanHqRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell DhanHQ status", err);
                      }
                      try {
                        const nubraRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_nubra");
                        setSellNubraEnabled(nubraRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell Nubra status", err);
                      }
                      // Refresh Pledge Plus statuses
                      try {
                        const ppDhanRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhanhq");
                        setSellPledgePlusDhanHqEnabled(ppDhanRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus DhanHQ status", err); }
                      try {
                        const ppFyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_fyers");
                        setSellPledgePlusFyersEnabled(ppFyersRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus Fyers status", err); }
                      try {
                        const ppZerodhaRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_zerodha");
                        setSellPledgePlusZerodhaEnabled(ppZerodhaRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus Zerodha status", err); }
                    }}
                    className="p-1 rounded-md hover:bg-[#14161f] transition text-gray-300 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                {/* SELL MODULE */}
                {/* <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell",
                      action: sellModule ? "disable" : "enable",
                      onConfirm: handleToggleSell
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell Module</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellModule ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellModule ? "ON" : "OFF"}
                  </span>
                </div> */}

                {/* EXIT MODULE */}
                {/* <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "exit",
                      action: exitModule ? "disable" : "enable",
                      onConfirm: handleToggleExit
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Exit Module</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${exitModule ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {exitModule ? "ON" : "OFF"}
                  </span>
                </div> */}

                {/* SELL XTS (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_xts",
                      action: sellXtsEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellXts
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell XTS (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellXtsEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellXtsEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL XTS ANURAG (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_xts_anurag",
                      action: sellXtsAnuragEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellXtsAnurag
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell XTS Anurag (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellXtsAnuragEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellXtsAnuragEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL FYERS (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_fyers",
                      action: sellFyersEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellFyers
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell Fyers (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellFyersEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellFyersEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL DHANHQ (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_dhanhq",
                      action: sellDhanHqEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellDhanHq
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell DhanHQ (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellDhanHqEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellDhanHqEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL NUBRA MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_nubra",
                      action: sellNubraEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellNubra
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell Nubra (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellNubraEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellNubraEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE PLUS — DHANHQ */}
                <div className="mt-2 px-3 py-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Pledge Plus</span>
                </div>
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_dhanhq",
                      action: sellPledgePlusDhanHqEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusDhanHq
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>PP Sell DhanHQ</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusDhanHqEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusDhanHqEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE PLUS — FYERS */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_fyers",
                      action: sellPledgePlusFyersEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusFyers
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>PP Sell Fyers</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusFyersEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusFyersEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE PLUS — ZERODHA */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_zerodha",
                      action: sellPledgePlusZerodhaEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusZerodha
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>PP Sell Zerodha</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusZerodhaEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusZerodhaEnabled ? "ON" : "OFF"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header >

      {/* GLOBAL CONTROLS TOOLBAR */}
      <div className="w-full bg-[#1a1d29] border-b border-gray-800 px-4 py-2 flex items-center justify-between gap-3 shrink-0">

        {/* === MARKET CLOCK + UTILITIES === */}
        <div className="flex items-center gap-3">
          {/* Clock widget */}
          <div className="flex items-center gap-2 bg-[#0f1116] px-3 py-1.5 rounded-lg border border-gray-700/60 shadow-inner">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor} ${marketStatus === "open" ? "animate-pulse" : ""}`} />
            <div className="flex flex-col leading-none">
              <span className={`font-mono font-bold text-base tracking-wider ${clockColor}`}>{istTime}</span>
              <span className="text-[9px] text-gray-500 font-semibold uppercase tracking-widest">{istDate} · IST</span>
            </div>
            <span className={`hidden md:inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
              marketStatus === "open" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" :
              marketStatus === "pre" || marketStatus === "post" ? "text-amber-400 border-amber-500/30 bg-amber-500/10" :
              "text-gray-500 border-gray-600/30 bg-gray-500/10"
            }`}>{marketStatusLabel}</span>
          </div>

          {/* Divider */}
          <div className="w-px h-7 bg-gray-700/50" />

          {/* ⚡ Potential Trades Button */}
          <button
            onClick={() => setIsPotentialTradesModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 hover:text-blue-300 text-[11px] font-bold uppercase tracking-wide rounded border border-blue-500/30 transition-all"
          >
            <Zap className="w-3.5 h-3.5 text-white" fill={groupedNudges.length > 0 ? "white" : "none"} />
            <span>Potential Trades</span>
            {groupedNudges.length > 0 && (
              <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-black">{groupedNudges.length}</span>
            )}
          </button>

          {/* Nudge O Toggle */}
          <button
            onClick={handleToggleNudgesO}
            title={nudgesOEnabled ? "Nudge O: ON (click to disable)" : "Nudge O: OFF (click to enable)"}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded border transition-all ${
              nudgesOEnabled
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30"
                : "bg-gray-800 text-gray-500 border-gray-700 hover:text-gray-300 hover:bg-gray-700"
            }`}
          >
            <Bell className="w-3.5 h-3.5 text-white" fill={nudgesOEnabled ? "white" : "none"} />
            <span>Nudge O</span>
            {nudgesOEnabled && !nudgesPEnabled && (
              <div className="flex items-center gap-1 pl-1 border-l border-emerald-500/30">
                <span className="text-gray-400">₹</span>
                <input
                  type="number"
                  value={nudgeThreshold}
                  onClick={e => e.stopPropagation()}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNudgeThreshold(val === "" ? "" : Number(val));
                  }}
                  className="w-12 bg-transparent border-none text-emerald-400 font-bold text-[11px] focus:outline-none"
                />
              </div>
            )}
          </button>

          {/* Nudge P Toggle */}
          <button
            onClick={handleToggleNudgesP}
            title={nudgesPEnabled ? "Nudge P: ON (click to disable)" : "Nudge P: OFF (click to enable)"}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded border transition-all ${
              nudgesPEnabled
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30"
                : "bg-gray-800 text-gray-500 border-gray-700 hover:text-gray-300 hover:bg-gray-700"
            }`}
          >
            <Bell className="w-3.5 h-3.5 text-white" fill={nudgesPEnabled ? "white" : "none"} />
            <span>Nudge P</span>
            {nudgesPEnabled && (
              <div className="flex items-center gap-1 pl-1 border-l border-emerald-500/30">
                <span className="text-gray-400">₹</span>
                <input
                  type="number"
                  value={nudgeThreshold}
                  onClick={e => e.stopPropagation()}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNudgeThreshold(val === "" ? "" : Number(val));
                  }}
                  className="w-12 bg-transparent border-none text-emerald-400 font-bold text-[11px] focus:outline-none"
                />
              </div>
            )}
          </button>

          {/* Signal Update Alert Toggle */}
          <button
            onClick={handleToggleSignalAlert}
            title={signalAlertEnabled ? "Signal Update Alert: ON (click to disable)" : "Signal Update Alert: OFF (click to enable)"}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded border transition-all ${
              signalAlertEnabled
                ? "bg-violet-500/20 text-violet-400 border-violet-500/30 hover:bg-violet-500/30"
                : "bg-gray-800 text-gray-500 border-gray-700 hover:text-gray-300 hover:bg-gray-700"
            }`}
          >
            <Radio className={`w-3.5 h-3.5 ${signalAlertEnabled ? "text-violet-400 animate-pulse" : "text-gray-500"}`} />
            <span>Signal Update Alert</span>
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Bot Status */}
          <div className="flex items-center gap-2 bg-[#0f1116] px-3 py-1.5 rounded border border-gray-700 hidden md:flex">
            <span className={`w-2 h-2 rounded-full ${wsStatus === "open" ? "bg-emerald-400 animate-pulse" : "bg-red-500"}`}></span>
            <span className="text-gray-300 text-xs font-medium">
              {wsStatus === "open" ? "Bot Active" : "Disconnected"}
            </span>
          </div>

        {/* Actions Group */}
        <div className="flex items-center gap-3 bg-gray-800 rounded-lg p-1 border border-gray-700">
          <button
            onClick={handlePing}
            title="Ping Server"
            className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors border-r border-gray-700/50"
          >
            <Activity className="w-4 h-4" />
          </button>
          <button
            onClick={refreshAll}
            title="Refresh All Data"
            className="p-1.5 rounded hover:bg-gray-700 text-blue-400 hover:text-blue-300 transition-colors border-r border-gray-700/50"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleReconnect}
            title="Reconnect WebSocket"
            className={`p-1.5 rounded hover:bg-gray-700 transition-colors ${wsStatus === 'open' ? 'text-gray-400 hover:text-white' : 'text-red-400 hover:text-red-300'}`}
          >
            {wsStatus === 'open' ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
          </button>
        </div>
        </div>
      </div>

      <NudgeOverlay nudges={activeNudges} onDismiss={handleDismissNudge} />
      <SignalToastOverlay toasts={signalToasts} onDismiss={dismissSignalToast} />

      {isPotentialTradesModalOpen && (() => {
        const potentialTradeMode =
          activeTab === "positions" ? "positions" :
          activeTab === "orders"   ? "orders"    : "both";
        return (
          <PotentialTradesModal
            isOpen={isPotentialTradesModalOpen}
            onClose={() => setIsPotentialTradesModalOpen(false)}
            mode={potentialTradeMode}
            symbolStrategyBaseQty={positionViewData.symbolStrategyBaseQty}
          />
        );
      })()}

      {/* ------------------------- CONFIRMATION MODAL ------------------------- */}
      {
        confirmModal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-2">
            <div className="bg-[#1a1d29] rounded-xl p-6 max-w-md w-full text-center shadow-lg animate-fade-in">
              <h3 className="text-white text-lg font-semibold mb-4">
                {confirmModal.action === "disable" ? "Disable" : "Enable"} {
                  confirmModal.module === "sell" ? "Sell Module" :
                    confirmModal.module === "exit" ? "Exit Module" :
                      confirmModal.module === "sell_xts" ? "Sell XTS (Optimus)" :
                        confirmModal.module === "sell_fyers" ? "Sell Fyers (Optimus)" :
                          "Sell XTS Anurag (Optimus)"
                }?
              </h3>
              <p className="text-gray-400 mb-6">
                Are you sure you want to {confirmModal.action} this module?
              </p>
              <div className="flex flex-col sm:flex-row justify-around gap-2">
                <button
                  onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                  disabled={confirmModal.loading}
                  className="px-4 py-2 rounded-md bg-gray-700 text-gray-200 hover:bg-gray-600 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setConfirmModal(prev => ({ ...prev, loading: true }));
                    if (confirmModal.onConfirm) await confirmModal.onConfirm();
                    setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
                  }}
                  disabled={confirmModal.loading}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirmModal.loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Confirm</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* ------------------------- TAB CONTENT ------------------------- */}
      <div className="flex-1 overflow-y-auto relative">
        {activeTab === "positions" && <PositionView {...positionViewData} loading={isPositionsInitialLoading} clientData={accountViewData.data} orders={orders} />}
        {activeTab === "orders" && <OrdersView {...orderViewData} loading={isOrdersInitialLoading} clientData={accountViewData.data} />}
        {activeTab === "raw" && <RawPositionsView clientData={accountViewData.data} />}
        {activeTab === "broker_raw" && <BrokerRawPositionsView clientData={accountViewData.data} />}
        {activeTab === "exitposition" && <ExitRawPositionsView clientData={accountViewData.data} />}
        {activeTab === "account" && <AccountView data={accountViewData.data} loading={accountViewData.loading} />}
        {activeTab === "rejected" && <RejectedOrderView rejectedOrders={rejectedOrdersData} clientData={accountViewData.data} loading={rejectOrdersLoading} />}
        {activeTab === "rejected" && <RejectedOrderView rejectedOrders={rejectedOrdersData} clientData={accountViewData.data} loading={rejectOrdersLoading} />}
        {activeTab === "commodities" && <CommoditiesPositionsView {...commoditiesViewData} loading={isPositionsInitialLoading} clientData={accountViewData.data} positions={positionsArray} orders={orders} />}
        {activeTab === "pledge_plus_orders" && <PledgePlusOrdersView {...pledgePlusOrdersViewData} loading={isOrdersInitialLoading} orders={orders} positions={positionsArray} />}
        {activeTab === "backtest" && <BacktestView />}
      </div>
    </div >
  );

}
